import { ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { useActiveCnpjId, useServices } from '@/features/services/hooks';
import { useActiveShifts } from '@/features/shifts/hooks';
import type { ShiftWithTotals } from '@/features/shifts/schemas';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

function formatDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

export default function ActiveShiftsPage() {
  const { cnpjId, isLoading: loadingCnpj } = useActiveCnpjId();
  const { data: shifts = [], isLoading: loadingShifts } = useActiveShifts(cnpjId);
  const { data: services = [] } = useServices(cnpjId);
  const navigate = useNavigate();

  const serviceById = new Map(services.map((s) => [s.id, s]));

  const sorted = [...shifts].sort((a, b) =>
    a.shift.date.localeCompare(b.shift.date),
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Plantões Ativos</h1>
          <p className="text-sm text-(--color-fg-muted)">
            Selecione um plantão em andamento para registrar exames, editar ou
            encerrar.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/calendar">Ir para o calendário</Link>
        </Button>
      </header>

      {loadingCnpj || loadingShifts ? (
        <p className="text-sm text-(--color-fg-muted)">Carregando plantões…</p>
      ) : !cnpjId ? (
        <p className="text-sm text-(--color-fg-muted)">
          Nenhum CNPJ ativo. Cadastre um CNPJ em Configurações antes de iniciar
          plantões.
        </p>
      ) : sorted.length === 0 ? (
        <div className="rounded-md border border-dashed border-(--color-border) p-8 text-center">
          <p className="text-sm text-(--color-fg-muted)">
            Nenhum plantão ativo no momento.
          </p>
          <Button className="mt-3" asChild>
            <Link to="/calendar">Abrir calendário</Link>
          </Button>
        </div>
      ) : (
        <ul
          className="flex flex-col gap-2"
          aria-label="Lista de plantões ativos"
        >
          {sorted.map((s) => (
            <li key={s.shift.id}>
              <ShiftRow
                item={s}
                serviceName={serviceById.get(s.shift.serviceId)?.name ?? '—'}
                serviceColor={
                  serviceById.get(s.shift.serviceId)?.color ?? '#64748b'
                }
                onOpen={() => navigate(`/shift/${s.shift.id}`)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface RowProps {
  item: ShiftWithTotals;
  serviceName: string;
  serviceColor: string;
  onOpen: () => void;
}

function ShiftRow({ item, serviceName, serviceColor, onOpen }: RowProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center justify-between gap-3 rounded-md border border-(--color-border) bg-(--color-surface) p-3 text-left transition-colors hover:border-(--color-accent)"
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="inline-flex h-2.5 w-2.5 flex-shrink-0 animate-pulse rounded-full"
          style={{ backgroundColor: serviceColor }}
          aria-hidden
        />
        <div className="min-w-0">
          <p className="truncate font-medium">{serviceName}</p>
          <p className="text-xs text-(--color-fg-muted)">
            {formatDate(item.shift.date)} · {item.totals.itemsCount}{' '}
            {item.totals.itemsCount === 1 ? 'item' : 'itens'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold tabular-nums">
          {currency.format(item.totals.total)}
        </span>
        <ChevronRight
          className="h-4 w-4 text-(--color-fg-muted) transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </div>
    </button>
  );
}
